// 3rd party libs
import React, { ReactElement } from 'react';
import theme from 'themes';
import styled from 'styled-components';

// Components
import { DataRenderer } from 'components/machine-health';
import { Checkbox, Column, KPICard, Typography, InformationTooltip } from 'components';

// Types
import { GroupPermission } from 'types/user-management';

import { useGetUserManagementPermissionsV2Query } from 'api';
import IcoInfo2 from 'icons/indexed-icons/IcoInfo2';
import { useTranslation } from 'react-i18next';

interface Props {
  onPermissionChange: (permissions: GroupPermission[]) => void;
  groupPermissions: GroupPermission[];
}

const StyledRow = styled.div`
  display: flex;
  grid-gap: 1.25rem;
  flex-wrap: wrap;
  padding: 0.625rem 0.75rem;
  border-bottom: 0.0625rem solid ${theme.colors.lightMediumGrey};
`;
const PermissionContainer = styled.div`
  margin-top: 0.75rem;
  header {
    padding: 0.75rem;
  }
`;
const CheckboxColumn = styled(Column)`
  p {
    margin: 0;
  }
`;
const Permission = ({ onPermissionChange, groupPermissions }: Props): ReactElement => {
  const { t } = useTranslation(['mh']);
  // Get all permissions and render permissions name.
  const {
    data: permissions,
    error: error,
    isFetching: isLoadingPermissions
  } = useGetUserManagementPermissionsV2Query();

  const updatePermission = (newPermission: GroupPermission, foundPermissionId: number) => {
    const newPermissions = [...groupPermissions];

    if (foundPermissionId !== -1) {
      // permission exist before
      if (permissions) newPermissions[foundPermissionId] = newPermission;
    } else {
      // permission does not exist before
      newPermissions.push(newPermission);
    }

    onPermissionChange(newPermissions);
  };

  const getIndexOfPermission = (permissionId: string) => {
    return groupPermissions.findIndex((permission) => permission.permissionId === permissionId);
  };

  const renderPermissions = () => {
    return permissions?.map((permission, index) => {
      const permissionId = permission.id;
      // Find the permission for this row in Group Permission
      const groupPermissionItem = groupPermissions.find(
        (groupPermissions) => groupPermissions.permissionId === permissionId
      );
      return (
        <StyledRow key={index}>
          <Column size={10} height="18px" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Typography
              size="0.75rem"
              color={theme.colors.text.lightBlack}
              mb={0}
              style={{ fontWeight: 500 }}
            >
              {permission.name}
            </Typography>
            {permission.name === t('Overview Basic') && (
              <InformationTooltip
                tooltipText={
                  t(
                    'Enabling Overview Basic will automatically disable Dashboard, and vice versa — only one can be granted at a time.'
                  ) as string
                }
                placement="top"
                icon={<IcoInfo2 color={theme.colors.primaryBlue5} height={18} width={18} />}
                width="20rem"
                labelStyle={{
                  fontSize: '14px',
                  lineHeight: 1.2,
                  color: theme.colors.text.lightBlack,
                  wordBreak: 'break-word'
                }}
              />
            )}
          </Column>
          <CheckboxColumn size={1} height="18px">
            {permission.name !== 'Machine Management' &&
              permission.name !== 'Groups and User Management' &&
              permission.name !== 'Documentation Center' && (
                <Checkbox
                  width={18}
                  height={18}
                  checked={groupPermissionItem && groupPermissionItem.read}
                  onChange={(e: { target: { checked: boolean } }) => {
                    updatePermission(
                      {
                        read: e.target.checked,
                        write: groupPermissionItem?.write ?? false,
                        permissionId
                      },
                      getIndexOfPermission(permissionId)
                    );
                  }}
                />
              )}
          </CheckboxColumn>
          <CheckboxColumn size={1} height="18px">
            <Checkbox
              width={18}
              height={18}
              checked={groupPermissionItem && groupPermissionItem.write}
              onChange={(e: { target: { checked: boolean } }) => {
                updatePermission(
                  {
                    read: groupPermissionItem?.read ?? false,
                    write: e.target.checked,
                    permissionId
                  },
                  getIndexOfPermission(permissionId)
                );
              }}
            />
          </CheckboxColumn>
        </StyledRow>
      );
    });
  };
  return (
    <PermissionContainer>
      <KPICard
        hideHeaderBackgroundColor={true}
        showHeaderBottom={true}
        component={
          <>
            <Typography size="0.813rem" weight="bold" color={theme.colors.lightGrey8}>
              Modules
            </Typography>
            <Typography
              size="0.813rem"
              weight="bold"
              color={theme.colors.lightGrey8}
              style={{ marginLeft: '12.5rem' }}
            >
              Read
            </Typography>
            <Typography
              size="0.813rem"
              weight="bold"
              color={theme.colors.lightGrey8}
              style={{ marginRight: '0' }}
            >
              Write
            </Typography>
          </>
        }
      >
        <DataRenderer isLoading={isLoadingPermissions} error={error && 'Failed to load data'}>
          {renderPermissions()}
        </DataRenderer>
      </KPICard>
    </PermissionContainer>
  );
};

export default Permission;
